import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ArrowLeft, Save } from 'lucide-react';
import RichTextEditor from '../components/RichTextEditor';
import ImageUploadField from '../components/ImageUploadField';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ArticleFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'violent',
    featured: false,
    image_url: '',
    author: '',
    tags: []
  });
  const [tagInput, setTagInput] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && (!user || user.role !== 'admin')) {
      navigate('/admin');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (isEdit && user && user.role === 'admin') {
      fetchArticle();
    }
  }, [id, user]);

  const fetchArticle = async () => {
    try {
      const { data } = await axios.get(`${API}/articles/${id}`);
      setFormData({
        title: data.title,
        content: data.content,
        category: data.category,
        featured: data.featured,
        image_url: data.image_url,
        author: data.author,
        tags: data.tags || []
      });
    } catch (error) {
      console.error('Error fetching article:', error);
      setError('Failed to load article');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      if (isEdit) {
        await axios.put(`${API}/articles/${id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API}/articles`, formData, { withCredentials: true });
      }
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const addTag = (raw) => {
    const clean = raw.trim().toLowerCase();
    if (!clean) return;
    if (formData.tags.includes(clean)) return;
    setFormData(prev => ({ ...prev, tags: [...prev.tags, clean] }));
  };

  const removeTag = (tag) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(tagInput);
      setTagInput('');
    } else if (e.key === 'Backspace' && !tagInput && formData.tags.length > 0) {
      removeTag(formData.tags[formData.tags.length - 1]);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#FFF8F0] flex items-center justify-center">Loading...</div>;
  }

  if (!user || user.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FFF8F0]" data-testid="article-form-page">
      <SEO title="Article Editor" noindex />
      <div className="bg-white border-b border-[#E5E5E5] p-6">
        <div className="max-w-[1200px] mx-auto">
          <button
            onClick={() => navigate('/admin/dashboard')}
            data-testid="back-to-dashboard-button"
            className="flex items-center gap-2 text-[#8A8A8A] hover:text-[#E63946] transition-colors mb-4"
            
          >
            <ArrowLeft size={18} />
            Back to Dashboard
          </button>
          <h1 className="text-3xl font-black tracking-tighter text-[#0A0A0A]" >
            {isEdit ? 'EDIT ARTICLE' : 'CREATE ARTICLE'}
          </h1>
        </div>
      </div>
      
      <div className="max-w-[1200px] mx-auto p-6">
        <div className="bg-white border border-[#E5E5E5] p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] mb-2" >
                TITLE *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                data-testid="article-title-input"
                required
                className="w-full px-4 py-3 border border-[#E5E5E5] focus:outline-none focus:border-[#E63946] transition-colors"
                
                placeholder="Enter article title"
              />
            </div>
            
            <div>
              <label className="block text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] mb-2" >
                CONTENT *
              </label>
              <RichTextEditor
                value={formData.content}
                onChange={(html) => setFormData(prev => ({ ...prev, content: html }))}
                placeholder="Start writing your story. Use the toolbar to format text, add headings, quotes, links, or images."
              />
            </div>
            
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] mb-2" >
                  CATEGORY *
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  data-testid="article-category-select"
                  required
                  className="w-full px-4 py-3 border border-[#E5E5E5] focus:outline-none focus:border-[#E63946] transition-colors"
                  
                >
                  <option value="violent">Violent Crimes</option>
                  <option value="cyber">Cyber Crimes</option>
                  <option value="white-collar">Financial</option>
                  <option value="property">Family/Extramarital</option>
                  <option value="tragedy">Tragedy</option>
                  <option value="others">Others</option>
                </select>
              </div>
              
              <div>
                <label className="block text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] mb-2" >
                  AUTHOR *
                </label>
                <input
                  type="text"
                  name="author"
                  value={formData.author}
                  onChange={handleChange}
                  data-testid="article-author-input"
                  required
                  className="w-full px-4 py-3 border border-[#E5E5E5] focus:outline-none focus:border-[#E63946] transition-colors"
                  
                  placeholder="Author name"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] mb-2" >
                HERO IMAGE *
              </label>
              <ImageUploadField
                value={formData.image_url}
                onChange={(url) => setFormData(prev => ({ ...prev, image_url: url }))}
                required
              />
            </div>

            <div>
              <label className="block text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A] mb-2">
                TAGS
              </label>
              <div
                className="w-full min-h-[48px] px-3 py-2 border border-[#E5E5E5] focus-within:border-[#E63946] transition-colors flex flex-wrap items-center gap-2"
                data-testid="article-tags-container"
              >
                {formData.tags.map((tag) => (
                  <span
                    key={tag}
                    data-testid={`article-tag-chip-${tag}`}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#FEE2E2] text-[#DC2626] text-xs font-bold tracking-[0.1em] uppercase"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      data-testid={`article-tag-remove-${tag}`}
                      className="hover:bg-[#DC2626] hover:text-white w-4 h-4 flex items-center justify-center transition-colors"
                      aria-label={`Remove ${tag}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  onBlur={() => { if (tagInput.trim()) { addTag(tagInput); setTagInput(''); } }}
                  data-testid="article-tags-input"
                  placeholder={formData.tags.length === 0 ? "Type a tag and press Enter (e.g. fraud, phishing)" : "Add another..."}
                  className="flex-1 min-w-[160px] outline-none border-none p-1 text-sm"
                />
              </div>
              <p className="text-xs text-[#8A8A8A] mt-1">Press Enter or comma to add. Click × to remove.</p>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                name="featured"
                checked={formData.featured}
                onChange={handleChange}
                data-testid="article-featured-checkbox"
                className="w-5 h-5 border-2 border-[#E5E5E5] focus:outline-none focus:border-[#E63946]"
              />
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-[#8A8A8A]" >
                FEATURE THIS ARTICLE (TOP NEWS)
              </label>
            </div>
            
            {error && (
              <div className="p-3 bg-[#E63946] bg-opacity-10 border border-[#E63946] text-[#E63946] text-sm" data-testid="article-form-error" >
                {error}
              </div>
            )}
            
            <div className="flex gap-4">
              <button
                type="submit"
                disabled={saving}
                data-testid="article-save-button"
                className="flex items-center gap-2 px-6 py-3 bg-[#E63946] text-white font-bold text-sm tracking-[0.2em] uppercase hover:bg-[#d32f3e] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                
              >
                <Save size={16} />
                {saving ? 'SAVING...' : 'SAVE ARTICLE'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/admin/dashboard')}
                data-testid="article-cancel-button"
                className="px-6 py-3 border border-[#E5E5E5] text-[#8A8A8A] font-bold text-sm tracking-[0.2em] uppercase hover:border-[#E63946] hover:text-[#E63946] transition-colors"
                
              >
                CANCEL
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ArticleFormPage;
